import type { HitTestResult } from '@chartv2';
import { ChartAssetData, PriceHoverData } from '@toggle/chart';
import { mapEntity } from '@toggle/toggle';
import React from 'react';

import { LegendPrice } from '~/views/turbo-chart/components/legend-price/LegendPrice';

export interface SeriesLegendStatusProps {
  asset: ChartAssetData;
  isPrimary: boolean;
  legendColor: string;
  hoverHit?: HitTestResult;
}

/**
 * Right-hand side of the chartv2 legend row. Delegates to the turbo
 * chart's `LegendPrice` — which already chooses between a
 * `LegendLivePrice` and a `LastClosePrice` view, subscribes to live
 * prices, and formats currency suffixes.
 *
 * When `hoverHit` is provided (from the chart engine's crosshair
 * hit-test), the displayed price tracks the hovered data point.
 */
export const SeriesLegendStatus = ({
  asset,
  isPrimary,
  legendColor,
  hoverHit,
}: SeriesLegendStatusProps) => {
  const { entity, snakeMeta, ts, seriesType } = asset;

  if (!snakeMeta || ts.length === 0) {
    return null;
  }

  const mappedEntity = mapEntity(entity);

  const createHoverData = (hoverHit: HitTestResult): PriceHoverData => {
    const { point } = hoverHit;
    const time = new Date(hoverHit.point.time).toISOString();
    const currentPrice =
      'close' in point
        ? { ...point, time }
        : {
            time,
            open: point.value,
            high: point.value,
            low: point.value,
            close: point.value,
          };

    // `LastClosePrice` only reads `currentPrice`/`previousPrice`; the other
    // fields are required by `PriceHoverData` but unused on the hover path.
    return {
      chartAssetData: asset,
      currentPrice,
      priceFormatted: String(currentPrice.close),
    };
  };

  return (
    <LegendPrice
      entity={mappedEntity}
      snakeMeta={snakeMeta}
      ts={ts}
      legendColor={legendColor}
      isPrimary={isPrimary}
      isChartHovered={!!hoverHit}
      selectedSeriesType={seriesType}
      hoverData={hoverHit && createHoverData(hoverHit)}
    />
  );
};
